
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action } from "vuex-class";
import { cloneDeep } from "lodash";

// Types
import { UserPermissions, FeaturesList } from "@/store/types";
import {
	UserManagementState,
	UserDetails,
	UserRole,
	UserRolePermission,
	Permission
} from "@/store/user-management/types";

// SV components
import GenericTable, { TableHeader } from "@/components/table/generic-table.vue";
import { ModalItem } from "@/components/table/generic-update-modal.vue";
import UserRolePermissions from "./UserRolePermissions.vue";

// VueX namespaces
const Eventqueue = namespace("eventqueue");
const UserManagement = namespace("userManagement");

import api from "@/services/api.service";

// Utility extensions
import "@/scripts/array-operations";

@Component({
	components: {
		"generic-table": GenericTable
	}
})
export default class UserRoleSetup extends Vue {
	@Eventqueue.Getter public routingGroups: any[];
	@Mutation private setIsLoading: (loading: boolean) => void;

	@UserManagement.Action public getUserRoles: (groupId: number) => UserRole[];
	@UserManagement.Action public loadPermissions: () => void;
	@Eventqueue.Action public loadRoutingGroups: () => void;

	@Prop()
	public selectedTenantGroupId: number;
	private isLoading: boolean = false;

	public rolesList: UserRole[] = [];
	public roleColumns: TableHeader[] = [
		{
			title: "Role Name",
			key: "title",
			order: 1,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The role name",
			searchable: true,
			visible: true,
			dataType: "input"
		},
		{
			title: "Permissions",
			key: "permissions",
			order: 2,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "User role permissions",
			searchable: false,
			visible: true,
			dataType: "list",
			useCustomCell: true
		}
	];
	public roleModalItems: ModalItem[] = [
		{
			title: "Role Name",
			key: "title",
			dataType: "text"
		} as ModalItem,
		{
			title: "Permissions",
			key: "permissions",
			dataType: "component",
			data: UserRolePermissions,
			defaultValue: []
		}
	];

	public async mounted() {
		this.isLoading = true;
		await this.loadPermissions();
		await this.loadRoutingGroups();
		if (this.selectedTenantGroupId) {
			this.rolesList = await this.getUserRoles(this.selectedTenantGroupId);
		}
		this.isLoading = false;
	}

	public async updateUserRole(userRole: UserRole) {
		if (userRole) {
			this.permissionExtendedComplexTypes(userRole);

			await api.saveUserRole(cloneDeep(userRole), this.selectedTenantGroupId);
			this.rolesList = await this.getUserRoles(this.selectedTenantGroupId);
		}
	}

	/*
	 * Method to deal with complex extended permission fields
	 * Stringify array for saving in db, removing corasponding permission if array is empty.
	 */
	private permissionExtendedComplexTypes(userRole: UserRole) {
		if (!userRole || !userRole.permissions) return;

		const permissionsToRemove: number[] = [];
		userRole.permissions.forEach(function(permission: any, index: number, arr: any) {
			// Custom extended values that are of type Array/Object
			if (
				permission.extended &&
				(permission.extended.constructor === Array || permission.extended === "object")
			) {
				if (permission.extended.constructor === Array && permission.extended.length == 0) {
					permissionsToRemove.push(index);
				}
				arr[index].extended = JSON.stringify(permission.extended);
			}
		});

		permissionsToRemove.forEach(index => {
			userRole.permissions.splice(index, 1);
		});
	}

	public async deleteUserRole(deleteRole: UserRole) {
		await api.deleteUserRole(deleteRole.userRoleId, this.selectedTenantGroupId);
		this.rolesList = await this.getUserRoles(this.selectedTenantGroupId);
	}

	public permissionExtendedValueToString(rolePermission: UserRolePermission): string {
		if (!rolePermission.permission || !rolePermission.permission.extendedValueDataType) {
			return "";
		}

		if (rolePermission.permission.extendedValueDataType === "number") {
			return `${rolePermission.permission.extendedValueTitle}: ${rolePermission.extended}`;
		}

		if (rolePermission.permission.extendedValueDataType === "routinggroup") {
			const routingGroupID = parseInt(rolePermission.extended);
			const routingGroup = this.routingGroups.firstOrDefault(group => group.routingGroupID === routingGroupID);

			if (routingGroup) {
				return `${rolePermission.permission.extendedValueTitle}: ${routingGroup.title}`;
			} else {
				return `${rolePermission.permission.extendedValueTitle}: ${rolePermission.extended}`;
			}
		}

		if (rolePermission.permission.extendedValueDataType === "InsightsReports") {
			const insightsPermission = JSON.parse(rolePermission.extended);
			if (insightsPermission.length > 0) {
				return `${insightsPermission.length.toString()} ${
					insightsPermission.length == 1 ? "Report" : "Reports"
				}`;
			}
		}
		return "";
	}

	private onModalOpen(): void {
		this.setIsLoading(true);
	}

	@Watch("selectedTenantGroupId")
	private async onSelectedTenantGroupIdChange(newSelectedTenantGroupId: number) {
		this.rolesList = await this.getUserRoles(newSelectedTenantGroupId);
	}
}
